import React, { FC, useRef, useEffect, useState, memo } from 'react';
import * as eCharts from 'echarts';
import { useIntl, getLocale } from 'umi';

import './Distribution.scss';

interface DistributionProps {
  gameList: any[];
  isAllGame: boolean;
}

const Distribution: FC<DistributionProps> = (props) => {
  let myChart: any = null;
  const chartRef = useRef(null);
  const { gameList, isAllGame } = props;
  const [options, setOptions] = useState<any>();
  const { formatMessage } = useIntl();
  const locale = getLocale();
  // 自定义tooltip函数
  const gameTooltipFormatter = (
    item: any,
  ) => `<div class="distribution-chart-tooltip" style="background-color: rgba(0, 0, 0, 0.75)">
    <div class="distribution-chart-tooltip-title">
      <img
        src="${item?.cover}"
        width="24px"
        height="24px"
        alt=""
      />
      <span class="distribution-chart-tooltip-title-name">
        ${item?.name}
      </span>
    </div>
    <div class="distribution-chart-tooltip-item">
      <span class="distribution-chart-tooltip-item-label">${formatMessage({
        id: 'dbi_benchmark_lifetime',
      })}</span>
      <span></span>
    </div>
    <div class="distribution-chart-tooltip-item">
      <span class="distribution-chart-tooltip-item-label">${formatMessage({id: 'dbi_benchmark_mobile_arpdau'})}</span>
      <span class="distribution-chart-tooltip-item-score">
        <span>${item?.arpu?.toFixed(2)}</span>
      </span>
    </div>
    <div class="distribution-chart-tooltip-item">
      <span class="distribution-chart-tooltip-item-label">
        ${formatMessage({id: 'dbi_benchmark_mobile_average_days'})}
      </span>
      <span class="distribution-chart-tooltip-item-score">
        <span>${item?.lty?.toFixed(2)}</span>
      </span>
    </div>
  </div>`;

  useEffect(() => {
    // 游戏散点图数据
    let gameListSeries: any[] = [];
    // 游戏目标数据
    let targetGameList:any[] = [];
    if (isAllGame) {
      //Put in all games 开关 开 如果游戏大于20 散点图插入所有图片
      targetGameList = gameList;
    } else {
      //Put in all games 开关 关
      if (gameList?.length >= 20) {
        // 如果游戏大于20 散点图插入列表前20图片
        targetGameList = gameList?.slice(0, 20);
      } else {
        // 放默认大于20的图片
        targetGameList = gameList;
      }
    }

    targetGameList?.forEach((item) => {
      gameListSeries.push({
        name: item?.name,
        data: [[item?.lty, item?.arpu]], // 二维坐标点 lty为x轴坐标 arpu为Y轴坐标
        symbolSize: 24, // 图片的大小
        cursor: 'pointer',
        type: 'scatter',
        symbol: `image://${item?.cover}`,
        symbolOffset: ['50%', '-50%'], // 相对于自身偏移
        z: 3,
        emphasis: {
          scale: 1.5,
        },
        tooltip: {
          formatter: gameTooltipFormatter(item),
          extraCssText: 'border: none;',
          backgroundColor: 'rgba(255, 255, 255, 0)',
        },
      });
    });

    const gameOptions = {
      tooltip: {
        show: true,
        confine: true,
        trigger: 'item',
        showDelay: 10,
        hideDelay: 100,
        borderWidth: 0,
        borderColor: '#fff',
        position: 'right',
        padding: 0,
        backgroundColor: 'rgba(0,0,0,0)'
      },
      graphic: [
        {
          type: 'text',
          right: 0,
          bottom: 0,
          cursor: 'default',
          style: {
            text: formatMessage({ id: 'dbi_benchmark_average_play_days' }),
            fill: '#838697',
            fontWeight: 400,
            fontSize: 14,
          },
        },
        {
          type: 'text',
          right: 20,
          top: 80,
          cursor: 'default',
          style: {
            text: 'Strong GaaS',
            fill: '#838697',
            fontWeight: 700,
            fontSize: 14,
          },
        },
        {
          type: 'text',
          left: 80,
          bottom: 70,
          cursor: 'default',
          style: {
            text: 'Weak GaaS',
            fill: '#838697',
            fontWeight: 700,
            fontSize: 14,
          },
        },
      ],
      grid: {
        top: 36,
        bottom: 30,
        left: 0,
        // left: 32,
        right: 15,
        containLabel: true,
      },
      xAxis: [
        {
          type: 'value',
          // max: xAxisMax,
          splitLine: {
            show: false,
          },
          axisLine: {
            lineStyle: {
              color: '#BFBFBF',
            },
          },
          axisTick: {
            show: true,
          },
          axisLabel: {
            show: true, // 不显示坐标轴上的文字
          },
          boundaryGap: false, // 不留白，从原点开始
        },
      ],
      yAxis: {
        type: 'value',
        name: formatMessage({id: 'dbi_benchmark_mobile_arpdau'}),
        nameGap: 16,
        nameTextStyle: {
          color: '#838697',
          fontWeight: 400,
          fontSize: 14,
          padding: [0, 0, 4, 7],
        },
        splitLine: {
          show: false,
        },
        // max: yAxisMax,
        axisLine: {
          show: true,
          lineStyle: {
            color: '#BFBFBF',
          },
        },
        axisTick: {
          show: true,
        },
        axisLabel: {
          show: true,
        },
      },
      series: [
        ...gameListSeries,
        // ...inverseListSeries,
        // ...tierSeriesList,
        // ...levelXSeriesList,
        // ...levelYSeriesList,
        // ...gaasInfo,
      ],
    };
    setOptions(gameOptions);
  }, [gameList, locale, isAllGame]);

  const handleResize = () => {
    try {
      setTimeout(()=> {
        myChart.resize();
      }, 20)
     
    } catch (err) {}
  };

  useEffect(() => {
    if (myChart === null) {
      myChart = eCharts.init(chartRef!.current!);
    }
    options && myChart.setOption(options, true);
  }, [options]);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [myChart]);

  return (
    <div
      ref={chartRef}
      style={{ height: 425, width: '100%' }}
      className="distribution-chart-container"
    ></div>
  );
};

Distribution.defaultProps = {};

export default memo(Distribution);
